/*
 * improvemint-variant-picker.js — the one thing the sparse option matrix needs
 * that Liquid cannot do.
 *
 * The design, and the reason any of this exists, is in the header comment of
 * snippets/product-variant-picker.liquid. In one line: a consolidated Best Buy
 * line-up is ONE Shopify product whose size x series matrix has holes, one option
 * is the primary axis and always fully offered, and every other option shows only
 * the values that exist alongside the current selection. That show/hide is decided
 * in Liquid, server-side, so it is right before this file loads and right with
 * this file blocked.
 *
 * THE MOVE. Picking a primary value that is not built in the size currently
 *    selected has to land on a variant that exists. Liquid stamps the answer on
 *    the input as `data-im-move-<option position>="<value index>"`; this file
 *    applies it in the CAPTURE phase of `change`, on `document`, which runs before
 *    variant-selects' own capture listener on the element — so by the time Hyper
 *    reads `selectedOptionValues` the DOM already holds a combination that exists,
 *    and the section is never re-rendered into "Unavailable".
 *
 * It moves inputs and nothing else. It does not compute which value to move to,
 * does not know what a variant is, and holds no copy of the matrix: getting the
 * rule wrong in a second place is exactly what improvemint-bb-sku.liquid was
 * extracted to prevent.
 *
 * THE SCROLLER IS NO LONGER HERE. `<im-option-scroller>` — the arrows that appear
 * only while there is somewhere to scroll — shipped inside this file on
 * 2026-09-25 and moved out the same day to assets/improvemint-scroller.js, when
 * the vendor landing page's category bar became its second caller. Nothing about
 * the PDP changed: the class is byte-for-byte the one that was here, and
 * layout/theme.liquid loads it on every page, so this file no longer defines or
 * needs it. `selectValueAt` still calls `scroller.bring(...)`, guarded on the
 * METHOD rather than the element, so a blocked or not-yet-upgraded scroller costs
 * the move nothing.
 *
 * COMPOSING WITH #185. That PR made the Best Buy SKU follow the SELECTED VARIANT
 * (snippets/improvemint-bb-sku.liquid) and had improvemint-pickup.js rewrite
 * `data-im-sku` off the bubbling `change` on `input[name="id"]`, which
 * `product-info.js` fires from `updateVariantInputs` after the fetch lands. This
 * file runs strictly BEFORE that fetch is built, and the move is the reason the
 * fetch resolves to a real variant at all — so `updateVariantInputs` gets a variant
 * id, fires its change, and the fulfilment widget re-asks for the moved-to SKU. A
 * combination with no variant is the one case where `updateVariantInputs` writes an
 * empty id, and avoiding that is the point.
 *
 * The picker snippet renders on the PDP and in quick-view, so this file can be
 * injected twice on one page. `window.__improvemintVariantPicker` guards that: a
 * second execution must not add a second `change` listener, or one move would be
 * applied and then read back as "nothing moved" and the live region would be
 * wiped.
 */
(function () {
  'use strict';

  var MOVE_PREFIX = 'data-im-move-';

  /* ------------------------------------------------------------------ the move */

  /*
   * `variant-selects` scopes everything: `product-info.js` inserts the re-rendered
   * element as a previous sibling and only removes the old one 500ms later
   * (HTMLUpdateUtility.viewTransition), so for half a second a PDP holds two of
   * them. Walking up from the input that actually changed is the only way to be
   * sure which one is being driven.
   */
  function onChange(event) {
    var input = event.target;
    if (!input || !input.closest) return;

    var root = input.closest('variant-selects');
    if (!root) return;

    /*
     * A <select> carries the move table on its <option>, because that is the node
     * the value belongs to — and it is also what variant-selects passes on as the
     * event target (`getInputForEventTarget`).
     */
    var source = input;
    if (input.tagName === 'SELECT') source = input.selectedOptions[0];
    if (!source || !source.getAttribute) return;

    var moved = applyMoves(root, source);
    announce(root, moved ? source.getAttribute('data-im-move-note') : '');
  }

  function applyMoves(root, source) {
    var attributes = source.attributes;
    var moved = false;

    for (var i = 0; i < attributes.length; i++) {
      var name = attributes[i].name;
      if (name.indexOf(MOVE_PREFIX) !== 0) continue;

      var position = name.slice(MOVE_PREFIX.length);
      // `data-im-move-note` shares the prefix and is not a position.
      if (!/^[0-9]+$/.test(position)) continue;

      if (selectValueAt(root, position, attributes[i].value)) moved = true;
    }

    return moved;
  }

  /*
   * Returns true only when something actually changed, so a move table that is
   * already satisfied does not claim a move the shopper never saw.
   */
  function selectValueAt(root, position, valueIndex) {
    var field = root.querySelector('[data-im-option-position="' + position + '"]');
    if (!field) return false;

    var target = field.querySelector('[data-im-value-index="' + valueIndex + '"]');
    if (!target) return false;

    if (target.tagName === 'OPTION') {
      var select = target.closest('select');
      if (!select) return false;

      var current = select.querySelector('option[selected]');
      if (current === target) return false;
      if (current) current.removeAttribute('selected');

      /*
       * The value being moved TO is by definition one that does not exist with the
       * outgoing primary value, so Liquid rendered it hidden and disabled. Clearing
       * both is what makes it selectable; the re-render replaces the whole element
       * a moment later with the state computed for the new selection.
       */
      target.removeAttribute('hidden');
      target.removeAttribute('disabled');
      target.setAttribute('selected', 'selected');
      // `selectedOptionValues` reads the ATTRIBUTE; the property is what the
      // shopper sees. Both, or the picker and the fetch disagree.
      select.value = target.value;
      return true;
    }

    if (target.checked) return false;
    target.checked = true;

    /*
     * The value being moved TO is one that does not exist with the OUTGOING
     * primary value, so Liquid rendered its card hidden. Leaving it hidden for the
     * ~300ms the section takes to come back shows the dependent option with
     * nothing checked at all, which reads as "your size was cleared" — the exact
     * impression the move exists to avoid. Revealed here; the re-render then
     * replaces the element with the state computed for the new selection, so this
     * is a stopgap for one frame and never the lasting answer.
     */
    reveal(target);
    reveal(target.nextElementSibling);

    /*
     * And bring it on screen. The cards scroll horizontally now, so a move can
     * land on a card that is off to the right — a selection that changed out of
     * sight is the silent failure GP's "make it visible" rules out, live region or
     * not. Guarded on the method rather than the element: the scroller may not have
     * been upgraded yet when a change arrives this early.
     */
    var scroller = target.closest('im-option-scroller');
    if (scroller && typeof scroller.bring === 'function') scroller.bring(target.nextElementSibling);

    /*
     * Hyper's `updateSelectedSwatchValue` only ever repaints the option the shopper
     * touched, and for a radio picker it looks for `[data-selected-value]`, which
     * the button/swatch legend does not render — so the echoed value beside the
     * option name is stale until the section comes back either way. For the option
     * that MOVED that staleness is a contradiction of the message we are about to
     * read out, so this one is corrected here.
     */
    var echo = field.querySelector('[data-selected-swatch-value]');
    if (echo) echo.textContent = target.value;
    return true;
  }

  /*
   * The live region lives OUTSIDE `variant-selects` (see the picker snippet), so it
   * is not destroyed by the re-render that the message describes. Cleared whenever
   * a change moves nothing, so it can never describe an older interaction.
   */
  function announce(root, message) {
    var scope = root.parentNode;
    var note = scope && scope.querySelector ? scope.querySelector('[data-im-variant-note]') : null;
    if (!note) return;
    note.textContent = message || '';
  }

  /*
   * `im-option--absent` is the load-bearing half (the class carries the
   * `display: none !important` that has to beat Hyper's `display: flex` on a
   * button label); `hidden` is rendered beside it so the markup says the same
   * thing. Both come off together or the card stays invisible.
   */
  function reveal(element) {
    if (!element) return;
    element.removeAttribute('hidden');
    element.classList.remove('im-option--absent');
  }

  /* ------------------------------------------------------------------ wiring up */

  if (!window.__improvemintVariantPicker) {
    window.__improvemintVariantPicker = true;
    document.addEventListener('change', onChange, true);
  }
})();
